//libraries
import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { HashLink } from 'react-router-hash-link';

// styles
import { Frame, PrimaryButton, Flex, ColumnedFlex, Label } from 'shared/styledComponents';
import { H1, Title } from 'shared/typography';

// custom components
import Layout from 'components/Layout';
import Checkbox from 'components/Form/Checkbox';
import SearchStoryPreview from './SearchStoryPreview';
import Icon from 'components/Icon';

// theme
import devices from 'theme/devices';
import colors from 'theme/colors';

// types
import { ProviderCriteria } from 'modules/ProviderCriteria/types';

const HeadingSectionWrapper = styled(Flex)`
  background-color: ${({ theme: { colors } }): string => colors.secondary};
  margin-bottom: 65px;
`;

const HeadingSection = styled(Frame)`
  padding-bottom: 45px;
  padding-top: 30px;

  @media ${devices.mobile} {
    padding-bottom: 35px;
  }
`;

const HeadingSectionContent = styled(Flex)`
  align-items: center;
  justify-content: space-between;

  @media ${devices.mobile} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeadingSectionTitle = styled(H1)`
  max-width: 600px;
  line-height: 82px;

  @media ${devices.mobile} {
    margin-bottom: 30px;
  }
`;

const HeadingText = styled(Title)<any>`
  display: none;

  @media ${devices.mobile} {
    display: block;
    margin-bottom: 35px;
  }
`;

const HeadingButtonsWrapper = styled(Flex)<any>`
  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

export const JoinCommunityButton = styled(PrimaryButton)(
  ({ theme: { colors } }) => `
  padding: 15px 16px;
  margin-left: 25px;
  background-color: ${colors.transparent};
  border: 2px solid ${colors.primary};
  color:${colors.primary};
  
  @media ${devices.mobile} {
    margin-left: 0;
    margin-top: 15px;
  }
  `,
);

const MainSectionWrapper = styled(Frame)<any>``;

const MainSection = styled(Flex)<any>`
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;

  @media ${devices.mobile} {
    flex-direction: column;
  }
`;

const Sidebar = styled(ColumnedFlex)<any>`
  @media ${devices.mobile} {
    width: 100%;
    margin-bottom: 40px;
  }
`;

const SidebarBlock = styled(ColumnedFlex)<any>`
  margin-bottom: 50px;

  :last-child {
    margin-bottom: 0;
  }

  @media ${devices.mobile} {
    margin-bottom: 20px;
  }
`;

const FiltersHeadingWrapper = styled(Flex)<any>`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const FiltersHeading = styled(Title)<any>`
  font-family: ${({ theme: { fonts } }): string => fonts.wigrumRegular};
  font-size: 14px;
  line-height: 1;
`;

const FilterIcon = styled(Icon)<any>`
  display: none;

  @media ${devices.mobile} {
    display: block;
  }
`;

const FiltersWrapper = styled(ColumnedFlex)`
  @media ${devices.mobile} {
    flex-direction: row;
    flex-wrap: wrap;
    border-bottom: ${({ theme: { colors } }): string => `1px solid ${colors.alto}`};
    padding-bottom: 10px;
  }
`;

const FilterWrapper = styled(Flex)`
  align-items: center;
  margin-bottom: 10px;

  :last-child {
    margin-bottom: 0;
  }

  @media ${devices.mobile} {
    width: 50%;
    margin-bottom: 10px;

    :last-child {
      margin-bottom: 10px;
    }
  }
`;

const FilterLabel = styled(Label)`
  font-size: 14px;
  margin-left: 10px;
`;

const StoriesWrapper = styled(Flex)`
  width: 100%;
  max-width: 950px;
  justify-content: space-between;
  align-items: flex-start;

  @media ${devices.mobile} {
    flex-wrap: wrap;
    max-width: 100%;
  }
`;

const StoryWrapper = styled(Flex)`
  width: 100%;
  max-width: 445px;
  flex-wrap: wrap;
`;

const StoriesHeading = styled(Title)<any>`
  display: flex;
  max-width: 375px;
  line-height: 34px;
  margin-bottom: 45px;

  @media ${devices.mobile} {
    display: none;
  }
`;

interface RealStoriesSearchProps {
  services: ProviderCriteria[];
  stages: ProviderCriteria[];
  isServicesFiltersOpened: boolean;
  isStagesFiltersOpened: boolean;
  onServicesFiltersOpenIconToggle: Dispatch<SetStateAction<boolean>>;
  onStagesFiltersOpenIconToggle: Dispatch<SetStateAction<boolean>>;
  stories: any;
}

const RealStoriesSearch = ({
  services,
  stages,
  isServicesFiltersOpened,
  isStagesFiltersOpened,
  onServicesFiltersOpenIconToggle,
  onStagesFiltersOpenIconToggle,
  stories,
}: RealStoriesSearchProps): JSX.Element => (
  <Layout>
    <HeadingSectionWrapper>
      <HeadingSection>
        <HeadingSectionContent>
          <HeadingSectionTitle>Real Stories</HeadingSectionTitle>
          <HeadingText size="medium">
            With so many stigmas around fertility and pregnancy, it can often feel like your struggles are a private
            burden to bear. You’re not in this alone. We offer guidance and support across all paths to parenthood, with
            shared personal and unfiltered experiences from a community who has collectively been through it all.
          </HeadingText>
          <HeadingButtonsWrapper>
            <PrimaryButton size="small">share your story</PrimaryButton>
            <JoinCommunityButton as={HashLink} to="#listYourPractice" size="small" smooth>
              join our community
            </JoinCommunityButton>
          </HeadingButtonsWrapper>
        </HeadingSectionContent>
      </HeadingSection>
    </HeadingSectionWrapper>
    <MainSectionWrapper>
      <MainSection>
        <Sidebar as="aside">
          <SidebarBlock>
            <FiltersHeadingWrapper>
              <FiltersHeading>Stage</FiltersHeading>
              <FilterIcon
                type="downArrow"
                color={colors.primary}
                width="15px"
                height="9px"
                onClick={onStagesFiltersOpenIconToggle}
              />
            </FiltersHeadingWrapper>
            <FiltersWrapper>
              {stages.map(({ id, value }: ProviderCriteria) => (
                <FilterWrapper key={id}>
                  <Checkbox name="termsAccepted" value={id} withoutForm />
                  <FilterLabel>{value}</FilterLabel>
                </FilterWrapper>
              ))}
            </FiltersWrapper>
          </SidebarBlock>
          <SidebarBlock>
            <FiltersHeadingWrapper>
              <FiltersHeading>Service</FiltersHeading>
              <FilterIcon
                type="downArrow"
                color={colors.primary}
                width="15px"
                height="9px"
                onClick={onServicesFiltersOpenIconToggle}
              />
            </FiltersHeadingWrapper>
            <FiltersWrapper>
              {services.map(({ id, value }: ProviderCriteria) => (
                <FilterWrapper key={id}>
                  <Checkbox name="termsAccepted" value={id} withoutForm />
                  <FilterLabel>{value}</FilterLabel>
                </FilterWrapper>
              ))}
            </FiltersWrapper>
          </SidebarBlock>
        </Sidebar>
        <StoriesWrapper>
          <StoryWrapper>
            {stories.map(({ id, imageUrl, quote, description, services }: any) => (
              <SearchStoryPreview
                key={id}
                imageUrl={imageUrl}
                quote={quote}
                description={description}
                services={services}
              />
            ))}
          </StoryWrapper>
          <StoryWrapper>
            <StoriesHeading size="medium">
              With so many stigmas around fertility and pregnancy, it can often feel like your struggles are a private
              burden to bear. You’re not in this alone. We offer guidance and support across all paths to parenthood,
              with shared personal and unfiltered experiences from a community who has collectively been through it all.
            </StoriesHeading>
            {stories.map(({ id, imageUrl, quote, description, services }: any) => (
              <SearchStoryPreview
                key={id}
                imageUrl={imageUrl}
                quote={quote}
                description={description}
                services={services}
              />
            ))}
          </StoryWrapper>
        </StoriesWrapper>
      </MainSection>
    </MainSectionWrapper>
  </Layout>
);

export default RealStoriesSearch;
